import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useVoiceConversation } from '../hooks/useVoiceConversation';
import { Mic, MicOff, Volume2, VolumeX, X } from 'lucide-react';
import Avatar3D from './Avatar3D';
import PhotoAvatar from './PhotoAvatar';

export default function VoiceConversationMode({ presenceId, presenceName, presenceGender = 'neutral', presencePhoto, token, language = 'tr', onClose }) {
  const {
    isConnected,
    isListening,
    isSpeaking,
    transcript,
    response,
    error,
    connect,
    disconnect,
    startRecording,
    stopRecording,
    interrupt
  } = useVoiceConversation(presenceId, token);

  const [isHoldingMic, setIsHoldingMic] = useState(false);
  const [autoListenMode, setAutoListenMode] = useState(true); // Auto-listen by default
  const [use3DAvatar, setUse3DAvatar] = useState(!presencePhoto); // Use 3D if no photo
  const audioRef = useRef(null);
  const hasStartedListeningRef = useRef(false);

  // Connect on mount
  useEffect(() => {
    console.log('🎙️ VoiceConversationMode: Component mounted, connecting...');
    connect(language);
    
    // Cleanup only on actual unmount
    return () => {
      console.log('🎙️ VoiceConversationMode: Component unmounting, disconnecting...');
      stopRecording(); // Stop recording before disconnect
      disconnect();
    };
    // Empty deps intentionally - we want this to run ONCE on mount
    // eslint-disable-next-line
  }, []);

  // Auto-start listening when connected
  useEffect(() => {
    if (isConnected && autoListenMode && !hasStartedListeningRef.current) {
      console.log('🎤 Auto-starting microphone in hands-free mode...');
      hasStartedListeningRef.current = true;
      
      // Wait a bit for UI to settle, then start recording
      const timer = setTimeout(() => {
        startRecording();
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [isConnected, autoListenMode, startRecording]);

  const handleMouseDown = () => {
    if (!autoListenMode) {
      setIsHoldingMic(true);
      startRecording();
    }
  };

  const handleMouseUp = () => {
    if (!autoListenMode) {
      setIsHoldingMic(false);
      stopRecording();
    }
  };

  const toggleManualRecording = () => {
    if (autoListenMode) {
      // In auto mode, clicking toggles recording
      if (isListening) {
        stopRecording();
      } else {
        startRecording();
      }
    }
  };

  const handleInterrupt = () => {
    interrupt();
  };

  const handleClose = () => {
    disconnect();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 z-50 flex flex-col">
      {/* Header */}
      <div className="p-6 flex justify-between items-center bg-black/20 backdrop-blur-sm">
        <div>
          <h2 className="text-2xl font-bold text-white">{presenceName}</h2>
          <p className="text-sm text-slate-300">
            {isConnected ? '🟢 Bağlı' : '🔴 Bağlanıyor...'}
          </p>
        </div>
        <button
          onClick={handleClose}
          className="w-12 h-12 rounded-full bg-red-500/20 hover:bg-red-500/30 flex items-center justify-center transition-colors"
        >
          <X className="text-white" size={24} />
        </button>
      </div>

      {/* Main Content - Avatar Area */}
      <div className="flex-1 flex flex-col items-center justify-center p-8">
        {/* 3D Avatar */}
        <motion.div
          className="relative w-full max-w-2xl aspect-square mb-8"
          animate={{
            scale: isSpeaking ? [1, 1.02, 1] : isListening ? [1, 1.01, 1] : 1
          }}
          transition={{
            duration: 0.5,
            repeat: isSpeaking || isListening ? Infinity : 0,
            ease: "easeInOut"
          }}
        >
          {/* Animated rings when speaking/listening */}
          <AnimatePresence>
            {(isSpeaking || isListening) && (
              <>
                <motion.div
                  className="absolute inset-0 rounded-full border-4 border-cyan-500/30"
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1.3, opacity: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
                <motion.div
                  className="absolute inset-0 rounded-full border-4 border-purple-500/30"
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1.3, opacity: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 2, repeat: Infinity, delay: 1 }}
                />
              </>
            )}
          </AnimatePresence>

          {/* Avatar Toggle Button */}
          <button
            onClick={() => setUse3DAvatar(!use3DAvatar)}
            className="absolute top-4 right-4 z-10 px-3 py-1.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-lg text-white text-sm font-medium transition-colors"
            title={use3DAvatar ? 'Fotoğrafı göster' : '3D modeli göster'}
          >
            {use3DAvatar ? '📸 Fotoğraf' : '🎭 3D Model'}
          </button>

          {/* Avatar Component - 3D or Photo */}
          <div className="w-full h-full">
            {use3DAvatar ? (
              <Avatar3D 
                isSpeaking={isSpeaking}
                audioElement={audioRef.current}
                gender={presenceGender}
                className="w-full h-full"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <PhotoAvatar
                  photoUrl={presencePhoto}
                  isSpeaking={isSpeaking}
                  audioLevel={isListening ? 0.5 : isSpeaking ? 0.7 : 0}
                  className="w-96 h-96"
                />
              </div>
            )}
          </div>

          {/* Status indicator */}
          <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 bg-black/80 backdrop-blur-sm px-6 py-2 rounded-full z-10">
            <p className="text-white text-sm font-medium">
              {isSpeaking ? '🗣️ Konuşuyor...' : isListening ? '👂 Dinliyor...' : '💤 Bekliyor'}
            </p>
          </div>
        </motion.div>

        {/* Hidden audio element for 3D avatar audio analysis */}
        <audio ref={audioRef} className="hidden" />

        {/* Transcript & Response */}
        <div className="w-full max-w-2xl space-y-4">
          {/* User's transcript */}
          {transcript && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-blue-500/20 backdrop-blur-sm rounded-2xl p-4 border border-blue-500/30"
            >
              <p className="text-xs text-blue-300 mb-1">Siz:</p>
              <p className="text-white">{transcript}</p>
            </motion.div>
          )}

          {/* AI's response */}
          {response && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-purple-500/20 backdrop-blur-sm rounded-2xl p-4 border border-purple-500/30"
            >
              <p className="text-xs text-purple-300 mb-1">{presenceName}:</p>
              <p className="text-white">{response}</p>
            </motion.div>
          )}
        </div>
      </div>

      {/* Bottom Controls */}
      <div className="p-8 flex flex-col items-center gap-6 bg-black/20 backdrop-blur-sm">
        {/* Mode Toggle */}
        <div className="flex items-center gap-3 mb-2">
          <button
            onClick={() => {
              setAutoListenMode(!autoListenMode);
              if (!autoListenMode) {
                // Switching to auto mode
                if (isConnected && !isListening) {
                  startRecording();
                }
              } else {
                // Switching to manual mode
                if (isListening) {
                  stopRecording();
                }
              }
            }}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              autoListenMode
                ? 'bg-green-500 text-white'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            {autoListenMode ? '🎤 Otomatik Dinleme' : '✋ Manuel Mod'}
          </button>
        </div>

        {/* Instructions */}
        <p className="text-slate-300 text-center text-sm">
          {autoListenMode
            ? isListening
              ? '🎤 Mikrofon aktif - Konuşabilirsiniz'
              : '⏸️ Dinleme duraklatıldı'
            : isListening
            ? 'Konuşmayı bitirmek için butonu bırakın'
            : 'Konuşmak için butona basılı tutun'}
        </p>

        {/* Main Microphone Button */}
        <div className="flex gap-6 items-center">
          {/* Interrupt button (when speaking) */}
          {isSpeaking && (
            <motion.button
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              onClick={handleInterrupt}
              className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-colors shadow-lg"
            >
              <VolumeX className="text-white" size={32} />
            </motion.button>
          )}

          {/* Microphone button */}
          <motion.button
            onMouseDown={!autoListenMode ? handleMouseDown : undefined}
            onMouseUp={!autoListenMode ? handleMouseUp : undefined}
            onTouchStart={!autoListenMode ? handleMouseDown : undefined}
            onTouchEnd={!autoListenMode ? handleMouseUp : undefined}
            onClick={autoListenMode ? toggleManualRecording : undefined}
            disabled={!isConnected || isSpeaking}
            className={`w-24 h-24 rounded-full flex items-center justify-center transition-all shadow-2xl ${
              isListening
                ? 'bg-red-500 scale-110 animate-pulse'
                : 'bg-gradient-to-br from-cyan-500 to-purple-500 hover:scale-105'
            } ${(!isConnected || isSpeaking) && 'opacity-50 cursor-not-allowed'}`}
            whileTap={{ scale: 0.95 }}
            title={autoListenMode ? 'Mikrofonu aç/kapat' : 'Konuşmak için basılı tut'}
          >
            {isListening ? (
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
              >
                <Mic className="text-white" size={48} />
              </motion.div>
            ) : (
              <MicOff className="text-white" size={48} />
            )}
          </motion.button>
        </div>

        {/* Error message */}
        {error && (
          <div className="bg-red-500/20 border border-red-500/30 rounded-lg px-4 py-2">
            <p className="text-red-300 text-sm">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
}
